import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";

import { FilesQueryKeys } from "@/Features/Files/files.query";

// By file path, not the EvidenceFiles barrel (it re exports the tab, which
// imports this module).
import { evidenceFilesQueryKeys } from "@/Features/EvidenceFiles/evidence-files.keys";

import { evidencePhotosQueryKeys } from "./evidence-photos.keys";
import { EvidencePhotosAPI } from "./evidence-photos.api";
import type {
  DeleteEvidencePhotoData,
  EvidencePhoto,
  RenameEvidencePhotoData,
} from "./evidence-photos.types";

// The API caps page_size at 1000 (EVIDENCE_PHOTO_MAX_PAGE_SIZE).
const PAGE_SIZE = 500;
// Signed links last a day; stay well under that.
const STALE_TIME = 1000 * 60 * 30;

/**
 * Every page of an item's photos, with `include_pending=true` so `uploading`
 * rows come back as placeholders (same as the storage photos tab).
 */
export async function fetchAllEvidencePhotos(
  evidence_id: number
): Promise<EvidencePhoto[]> {
  const photos: EvidencePhoto[] = [];
  let page: number | null = 1;
  while (page != null) {
    const res = await EvidencePhotosAPI.get(evidence_id, {
      page,
      page_size: PAGE_SIZE,
      include_pending: true,
    });
    if (!Array.isArray(res.data)) {
      throw new Error(
        `Failed to fetch photos for evidence item ${evidence_id}`
      );
    }
    photos.push(...res.data);
    if (res.nextPage != null && res.nextPage <= page) {
      throw new Error(
        `File list for evidence item ${evidence_id} returned nextPage ${res.nextPage} after page ${page}`
      );
    }
    page = res.nextPage;
  }
  return photos;
}

export function useGetEvidencePhotos(
  { evidence_id }: { evidence_id?: number },
  options?: { enabled?: boolean }
) {
  return useQuery<EvidencePhoto[]>({
    queryKey: evidencePhotosQueryKeys.list(evidence_id),
    queryFn: () => fetchAllEvidencePhotos(evidence_id as number),
    enabled: evidence_id != null && (options?.enabled ?? true),
    // The connection.js interceptor already toasts each failed attempt.
    retry: false,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: true,
    // The same rows can be renamed or deleted from Case Files, which only
    // invalidates the Files keys, so refetch whenever the tab mounts.
    refetchOnMount: "always",
  });
}

/**
 * The same rows show in Case Files under Evidence Files, so a change here
 * refreshes those lists too.
 */
const refreshAfterChange = async (
  queryClient: QueryClient,
  evidence_id: number
) => {
  await queryClient.invalidateQueries({
    queryKey: evidencePhotosQueryKeys.list(evidence_id),
  });
  queryClient.invalidateQueries({ queryKey: FilesQueryKeys.all });
};

export function useDeleteEvidencePhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DeleteEvidencePhotoData) =>
      EvidencePhotosAPI.delete(data),
    // No optimistic removal: the tile stays until the delete has finished.
    onSuccess: async (_data, { evidence_id, photo_uuid }) => {
      const queryKey = evidencePhotosQueryKeys.list(evidence_id);
      // an in flight list fetch could otherwise put the deleted row back
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<EvidencePhoto[]>(queryKey, (existing) =>
        existing?.filter((p) => p.uuid !== photo_uuid)
      );
      await refreshAfterChange(queryClient, evidence_id);
    },
  });
}

export function useRenameEvidencePhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RenameEvidencePhotoData) =>
      EvidencePhotosAPI.rename(data),
    onSuccess: async (_data, { evidence_id, photo_uuid, name }) => {
      const queryKey = evidencePhotosQueryKeys.list(evidence_id);
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<EvidencePhoto[]>(queryKey, (existing) =>
        existing?.map((p) => (p.uuid === photo_uuid ? { ...p, name } : p))
      );
      await refreshAfterChange(queryClient, evidence_id);
    },
  });
}

/**
 * Applies a finished upload to the cached list, the same way
 * `applyUploadedStoragePhoto` does: cancel an in flight fetch that could still
 * carry the row as `uploading`, then replace or append it. Refetches when the
 * list isn't cached yet or the result has no row.
 */
export async function applyUploadedEvidencePhoto(
  queryClient: QueryClient,
  evidence_id: number,
  photo?: EvidencePhoto
) {
  const queryKey = evidencePhotosQueryKeys.list(evidence_id);
  if (photo && queryClient.getQueryData(queryKey)) {
    await queryClient.cancelQueries({ queryKey });
    queryClient.setQueryData<EvidencePhoto[]>(queryKey, (existing) => {
      if (!existing) return existing;
      return existing.some((p) => p.uuid === photo.uuid)
        ? existing.map((p) => (p.uuid === photo.uuid ? photo : p))
        : [...existing, photo];
    });
    // The list is sorted by name server side; refetch so the new row moves
    // to its place instead of sitting at the end.
    queryClient.invalidateQueries({ queryKey });
  } else {
    await queryClient.invalidateQueries({ queryKey });
  }
  // The first upload creates the item's folder (the evidence Files tab
  // browses it).
  queryClient.invalidateQueries({
    queryKey: evidenceFilesQueryKeys.itemFolder(evidence_id),
  });
  queryClient.invalidateQueries({ queryKey: FilesQueryKeys.all });
}
